/*
 * Figma `126:523` — SA3-11 · Credit Operations.
 *
 * `TableTemplate`: balances, the top-up queue, and the owner you opened in a drawer.
 *
 * Credits are money owed in two directions and the frame's own metric strip says so: `spendable` is
 * what an organizer paid for in advance, `debtCredits` is what the platform already delivered and
 * has not been paid for. Showing one without the other reads healthy when it is not, so both travel
 * together in every view here — including the per-owner rows, where a large balance beside a large
 * debt is a different conversation from either alone.
 *
 * Confirming a top-up and granting credits both create value, so each states what it does before it
 * does it, and a grant cannot be sent without a note the server records.
 */
import { useState } from 'react';
import {
  Alert, Button, Card, ConfirmDialog, Drawer, FormDialog, KpiCard, StatusBadge, TableTemplate,
  TextField,
} from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import type { CreditLotDto, CreditTopupDto, PlatformCreditOverviewDto } from '../../../api';
import type { CreditOperationsState } from './useCreditOperations';
import { useCreditOperations } from './useCreditOperations';

type OwnerRow = PlatformCreditOverviewDto['owners'][number];

const baht = (n: number) => `฿${Math.round(n).toLocaleString('th-TH')}`;
const when = (iso: string) => new Date(iso).toLocaleString('th-TH');

const TOPUP_TONE: Record<CreditTopupDto['status'], BadgeTone> = {
  pending: 'neutral', awaiting_verification: 'warning', confirmed: 'success',
  rejected: 'danger', expired: 'neutral',
};

export function CreditOperationsScreen({ destination, state }: {
  destination: Destination;
  state: CreditOperationsState;
}) {
  const {
    overview, loading, loadError, error, busy, confirmTopup, rejectTopup, grant, reload,
    openOwner: loadOwner, closeOwner, ownerDetail, ownerBusy, adjustDebt, extendLot,
  } = state;
  const [openOwner, setOpenOwner] = useState<string | null>(null);
  const [confirmingTopup, setConfirmingTopup] = useState<CreditTopupDto | null>(null);
  const [grantCredits, setGrantCredits] = useState('');
  /* A year, because it is the length the paid tiers use — an operator may still change it. */
  const [grantValidDays, setGrantValidDays] = useState('365');
  const [grantNote, setGrantNote] = useState('');
  const [debtDelta, setDebtDelta] = useState('');
  const [debtReason, setDebtReason] = useState('');
  const [extending, setExtending] = useState<CreditLotDto | null>(null);
  const [extendTo, setExtendTo] = useState('');
  const [extendReason, setExtendReason] = useState('');

  const owners = overview?.owners ?? [];
  const topups = overview?.topups ?? [];
  const owner = openOwner ? owners.find((o) => o.ownerEmail === openOwner) ?? null : null;
  const waiting = topups.filter((t) => t.status === 'awaiting_verification' || t.status === 'pending');

  const columns: Column<OwnerRow>[] = [
    { key: 'owner', header: 'ผู้จัด', cell: (o) => <span className="jt-mono">{o.ownerEmail}</span> },
    { key: 'spendable', header: 'เครดิตคงเหลือ', align: 'end', width: '150px', cell: (o) => <span className="jt-mono">{o.spendable.toLocaleString('th-TH')}</span> },
    {
      key: 'debt',
      header: 'ค้างชำระ',
      align: 'end',
      width: '150px',
      cell: (o) => (
        <span className={o.debtCredits > 0 ? 'jt-mono jt-amount-owed' : 'jt-mono'}>
          {o.debtCredits.toLocaleString('th-TH')}
        </span>
      ),
    },
    { key: 'lots', header: 'ล็อต', align: 'end', width: '100px', cell: (o) => <span className="jt-mono">{o.lots}</span> },
    {
      key: 'expiring',
      header: 'ใกล้หมดอายุ',
      align: 'end',
      width: '160px',
      cell: (o) => (o.expiringSoon > 0
        ? <StatusBadge tone="warning">{`${o.expiringSoon.toLocaleString('th-TH')} เครดิต`}</StatusBadge>
        : <span className="jt-cell-sub">—</span>),
    },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, onRetry: reload }}
      kpis={[
        <KpiCard key="spendable" label="เครดิตคงเหลือรวม" value={(overview?.totalSpendable ?? 0).toLocaleString('th-TH')} />,
        <KpiCard
          key="debt"
          label="ค้างชำระรวม"
          value={(overview?.totalDebt ?? 0).toLocaleString('th-TH')}
          tone={(overview?.totalDebt ?? 0) > 0 ? 'warning' : undefined}
          meta="แพลตฟอร์มส่งมอบไปแล้วแต่ยังไม่ได้รับเงิน"
        />,
        <KpiCard
          key="queue"
          label="รอยืนยันการเติม"
          value={waiting.length.toLocaleString('th-TH')}
          tone={waiting.length > 0 ? 'danger' : undefined}
        />,
        <KpiCard key="owners" label="ผู้จัดที่มีเครดิต" value={owners.length.toLocaleString('th-TH')} />,
      ]}
      summary={
        <>
          {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
          {waiting.length > 0 && (
            <Card title={`คิวเติมเครดิต · ${waiting.length}`} sub="ยืนยันแล้วเครดิตจะเข้าบัญชีผู้จัดทันที">
              <ul className="jt-account-list">
                {waiting.map((topup) => (
                  <li key={topup.id} className="jt-band-row">
                    <div className="jt-band-copy">
                      <span className="jt-band-name">
                        {baht(topup.amountDueThb)} · {(topup as { ownerEmail?: string }).ownerEmail ?? '—'}
                      </span>
                      <span className="jt-band-usage">
                        ขอเมื่อ {when(topup.createdAt)}
                        {topup.carryoverDiscountThb > 0 && ` · หักยกยอด ${baht(topup.carryoverDiscountThb)}`}
                        {!topup.hasSlip && ' · ยังไม่ได้แนบสลิป'}
                      </span>
                    </div>
                    <div className="jt-inline-actions">
                      <StatusBadge tone={TOPUP_TONE[topup.status]}>{topup.status}</StatusBadge>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === topup.id}
                        onClick={() => void rejectTopup(topup.id, 'ปฏิเสธจากหน้า Credit Operations')}
                      >
                        ปฏิเสธ
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={busy === topup.id || !topup.hasSlip}
                        onClick={() => setConfirmingTopup(topup)}
                      >
                        ยืนยัน
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
              {waiting.some((t) => !t.hasSlip) && (
                <p className="jt-field-hint">รายการที่ยังไม่มีสลิปยืนยันไม่ได้ — รอผู้จัดแนบหลักฐานการโอนก่อน</p>
              )}
            </Card>
          )}
        </>
      }
      table={{
        label: 'เครดิตรายผู้จัด',
        columns,
        rows: owners,
        rowKey: (o) => o.ownerEmail,
        emptyTitle: 'ยังไม่มีผู้จัดที่ถือเครดิต',
        onRowClick: (o) => {
          setOpenOwner(o.ownerEmail);
          setGrantCredits(''); setGrantNote(''); setDebtDelta(''); setDebtReason('');
          void loadOwner(o.ownerEmail);
        },
        activeRowKey: openOwner,
        card: (o) => (
          <span className="jt-cell-stack">
            <span className="jt-mono">{o.ownerEmail}</span>
            <span className="jt-cell-sub jt-mono">คงเหลือ {o.spendable} · ค้าง {o.debtCredits}</span>
          </span>
        ),
      }}
      drawer={
        <>
          <Drawer
            open={!!owner}
            onClose={() => { setOpenOwner(null); closeOwner(); }}
            title={owner?.ownerEmail ?? ''}
          >
            {owner && (
              <>
                {owner.debtCredits > 0 && (
                  <Alert tone="warning" title={`ค้างชำระ ${owner.debtCredits.toLocaleString('th-TH')} เครดิต`}>
                    แพลตฟอร์มส่งมอบไปแล้วแต่ยังไม่ได้รับเงินส่วนนี้ — ดูควบคู่กับยอดคงเหลือเสมอ
                  </Alert>
                )}

                <Card title="ยอดปัจจุบัน">
                  <ul className="jt-order-lines">
                    <li><span>เครดิตคงเหลือ</span><span className="jt-mono">{owner.spendable.toLocaleString('th-TH')}</span></li>
                    <li><span>ค้างชำระ</span><span className="jt-mono">{owner.debtCredits.toLocaleString('th-TH')}</span></li>
                    <li><span>ล็อตที่ถืออยู่</span><span className="jt-mono">{owner.lots}</span></li>
                    <li className="jt-order-total"><span>ใกล้หมดอายุ</span><span className="jt-mono">{owner.expiringSoon.toLocaleString('th-TH')}</span></li>
                  </ul>
                </Card>

                <Card title="ให้เครดิตเพิ่ม" sub="ใช้เมื่อต้องชดเชยหรือให้ตามข้อตกลงนอกระบบเติมเงิน">
                  <TextField
                    label="จำนวนเครดิต"
                    type="number"
                    min={1}
                    value={grantCredits}
                    onChange={(e) => setGrantCredits(e.target.value)}
                  />
                  {/*
                    * Asked for, not defaulted silently. The endpoint refuses a grant that names
                    * neither a package nor a validity, and a lot's expiry is a real decision — an
                    * operator who never chose one finds out on the day the credits stop working.
                    */}
                  <TextField
                    label="อายุการใช้งาน (วัน)"
                    type="number"
                    min={1}
                    value={grantValidDays}
                    onChange={(e) => setGrantValidDays(e.target.value)}
                    hint={Number(grantValidDays) > 0
                      ? `หมดอายุ ${new Date(Date.now() + Number(grantValidDays) * 86_400_000).toISOString().slice(0, 10)}`
                      : 'เครดิตที่ให้มีวันหมดอายุเสมอ'}
                  />
                  <TextField
                    label="เหตุผลของการให้เครดิต"
                    requiredMark
                    value={grantNote}
                    onChange={(e) => setGrantNote(e.target.value)}
                    hint="บันทึกลง ledger ถาวร — เขียนให้คนอ่านย้อนหลังเข้าใจว่าทำไม"
                  />
                  <Alert tone="warning" title="การให้เครดิตคือการสร้างมูลค่า">
                    เครดิตที่ให้ไปใช้จ่ายได้ทันทีเหมือนเครดิตที่ซื้อ และย้อนกลับไม่ได้จากหน้านี้
                  </Alert>
                  <div className="jt-inline-actions">
                    <Button
                      variant="primary"
                      disabled={busy === owner.ownerEmail || !Number(grantCredits)
                        || !Number(grantValidDays) || !grantNote.trim()}
                      onClick={async () => {
                        await grant(
                          owner.ownerEmail, Number(grantCredits), grantNote.trim(), Number(grantValidDays),
                        );
                        setGrantCredits('');
                        setGrantNote('');
                      }}
                    >
                      {busy === owner.ownerEmail ? 'กำลังให้เครดิต…' : 'ให้เครดิต'}
                    </Button>
                  </div>
                </Card>

                {/*
                  * Debt moves in both directions and neither is routine: a positive delta bills an
                  * organizer for something delivered off-system, a negative one writes it off. The
                  * server records the reason against the organizer, so the box is required here
                  * rather than checked only server-side.
                  */}
                <Card
                  title="ปรับยอดค้างชำระ"
                  sub="บวกคือเพิ่มยอดที่ผู้จัดค้าง · ลบคือตัดหนี้ออก — ทุกครั้งถูกบันทึกพร้อมเหตุผล"
                >
                  <TextField
                    label="จำนวนเครดิต (ใส่ค่าติดลบเพื่อตัดหนี้)"
                    type="number"
                    value={debtDelta}
                    onChange={(e) => setDebtDelta(e.target.value)}
                    hint={`ยอดค้างตอนนี้ ${owner.debtCredits.toLocaleString('th-TH')} เครดิต`}
                  />
                  <TextField
                    label="เหตุผลของการปรับยอด"
                    value={debtReason}
                    onChange={(e) => setDebtReason(e.target.value)}
                    hint="จะถูกบันทึกไว้กับผู้จัดรายนี้"
                  />
                  <div className="jt-inline-actions">
                    <Button
                      variant="destructive"
                      disabled={busy === owner.ownerEmail || !Number(debtDelta) || !debtReason.trim()}
                      onClick={async () => {
                        await adjustDebt(owner.ownerEmail, Number(debtDelta), debtReason.trim());
                        setDebtDelta('');
                        setDebtReason('');
                      }}
                    >
                      {busy === owner.ownerEmail ? 'กำลังปรับยอด…' : 'ปรับยอดค้างชำระ'}
                    </Button>
                  </div>
                </Card>

                <Card
                  title="ล็อตเครดิต"
                  sub="ต่ออายุได้ทีละล็อต — ล็อตที่หมดอายุแล้วต่อไม่ได้ ต้องให้เครดิตใหม่แทน"
                >
                  {ownerBusy && <p className="jt-field-hint">กำลังโหลดล็อต…</p>}
                  {!ownerBusy && (ownerDetail?.lots.length ?? 0) === 0 && (
                    <p className="jt-field-hint">ผู้จัดรายนี้ยังไม่มีล็อตเครดิต</p>
                  )}
                  {(ownerDetail?.ownerEmail === owner.ownerEmail ? ownerDetail.lots : []).map((lot) => (
                    <div key={lot.id} className="jt-lot-row">
                      <ul className="jt-order-lines">
                        <li>
                          <span>คงเหลือ</span>
                          <span className="jt-mono">
                            {lot.creditsRemaining.toLocaleString('th-TH')} / {lot.creditsTotal.toLocaleString('th-TH')}
                          </span>
                        </li>
                        <li>
                          <span>หมดอายุ</span>
                          <span className="jt-mono">{lot.expiresAt ? lot.expiresAt.slice(0, 10) : 'ไม่มีกำหนด'}</span>
                        </li>
                      </ul>
                      <div className="jt-inline-actions">
                        <StatusBadge tone={lot.live ? 'success' : 'neutral'}>
                          {lot.live ? 'ใช้ได้' : 'ใช้ไม่ได้แล้ว'}
                        </StatusBadge>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!lot.live || busy === lot.id}
                          onClick={() => {
                            setExtending(lot);
                            setExtendTo(lot.expiresAt ? lot.expiresAt.slice(0, 10) : '');
                            setExtendReason('');
                          }}
                        >
                          ต่ออายุ
                        </Button>
                      </div>
                    </div>
                  ))}
                </Card>
              </>
            )}
          </Drawer>

          {/*
            * Extending is a dialog rather than an inline field because the date and the reason
            * belong to one decision, and the reason is recorded against the lot — not the
            * organizer — so it has to travel with the date that caused it.
            */}
          <FormDialog
            open={extending !== null}
            onClose={() => setExtending(null)}
            onSubmit={() => {
              const lot = extending;
              if (!lot || !extendTo || !extendReason.trim()) return;
              void extendLot(lot.id, new Date(`${extendTo}T23:59:59Z`).toISOString(), extendReason.trim())
                .then(() => {
                  setExtending(null);
                  if (openOwner) void loadOwner(openOwner);
                });
            }}
            title="ต่ออายุล็อตเครดิต"
            description={extending
              ? `คงเหลือ ${extending.creditsRemaining.toLocaleString('th-TH')} เครดิต · เดิมหมดอายุ ${extending.expiresAt?.slice(0, 10) ?? 'ไม่มีกำหนด'}`
              : undefined}
            submitLabel="ต่ออายุล็อตนี้"
            /* `onSubmit` already refuses without both; the button now says so instead of no-opping. */
            submitDisabled={!extendTo || !extendReason.trim()}
            busy={busy === extending?.id}
            error={error || null}
          >
            <TextField
              label="หมดอายุใหม่"
              type="date"
              value={extendTo}
              onChange={(e) => setExtendTo(e.target.value)}
              hint="ต้องเป็นวันหลังจากวันหมดอายุเดิม"
            />
            <TextField
              label="เหตุผลของการต่ออายุ"
              value={extendReason}
              onChange={(e) => setExtendReason(e.target.value)}
              hint="บันทึกไว้กับล็อตนี้"
            />
          </FormDialog>

          <ConfirmDialog
            open={!!confirmingTopup}
            onClose={() => setConfirmingTopup(null)}
            onConfirm={() => {
              const target = confirmingTopup;
              setConfirmingTopup(null);
              if (target) void confirmTopup(target.id);
            }}
            title={`ยืนยันการเติมเครดิต ${confirmingTopup ? baht(confirmingTopup.amountDueThb) : ''}`}
            confirmLabel="ยืนยันการเติม"
            tone="warning"
            consequences={[
              'เครดิตจะเข้าบัญชีผู้จัดทันทีและใช้จ่ายได้เลย',
              'ตรวจว่าเงินเข้าบัญชีแพลตฟอร์มจริงก่อนยืนยัน — หน้านี้ไม่ได้ตรวจสลิปให้อัตโนมัติ',
              'ยกเลิกภายหลังต้องปรับยอดด้วยมือ ซึ่งจะทิ้งร่องรอยไว้ใน ledger',
            ]}
          />
        </>
      }
    />
  );
}

export function CreditOperationsDestination({ token, destination }: { token: string; destination: Destination }) {
  const state = useCreditOperations(token);
  return <CreditOperationsScreen destination={destination} state={state} />;
}
